import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ScrollText, Calendar } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const GrievanceBlock = ({ contact }) => {
  if (!contact) return null;
  return (
    <div
      className="my-3 p-4 rounded-lg border border-slate-200 bg-slate-50"
      data-testid="legal-grievance-contact-block"
    >
      <div className="space-y-1 text-sm">
        <div><span className="font-semibold text-slate-700">Name:</span> <span className="text-slate-900">{contact.name}</span></div>
        <div><span className="font-semibold text-slate-700">Email:</span> <a href={`mailto:${contact.email}`} className="text-blue-700 hover:underline">{contact.email}</a></div>
        <div><span className="font-semibold text-slate-700">Phone:</span> <a href={`tel:${contact.phone}`} className="text-blue-700 hover:underline">{contact.phone}</a></div>
      </div>
    </div>
  );
};

const Section = ({ section, contact }) => (
  <section
    id={`tc-${section.id}`}
    data-testid={`legal-section-${section.id}`}
    className={`mb-6 ${section.highlight ? 'p-5 rounded-lg border border-amber-300 bg-amber-50' : ''}`}
  >
    <h3 className="text-lg font-semibold text-slate-900 mb-3">{section.title}</h3>
    {(section.body || []).map((p, i) =>
      p === '__GRIEVANCE_CONTACT_BLOCK__' ? (
        <GrievanceBlock key={i} contact={contact} />
      ) : (
        <p
          key={i}
          className={`text-sm leading-relaxed mb-2.5 ${section.highlight ? 'text-slate-800 font-medium' : 'text-slate-700'}`}
        >
          {p}
        </p>
      )
    )}
  </section>
);

export default function LegalPage() {
  const navigate = useNavigate();
  const [doc, setDoc] = useState(null);
  const [contact, setContact] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${API}/api/tc/document`)
      .then((r) => r.ok ? r.json() : Promise.reject(r))
      .then((j) => setDoc(j))
      .catch(() => setError(true));
    fetch(`${API}/api/tc/grievance-contact-public`)
      .then((r) => r.ok ? r.json() : null)
      .then((j) => { if (j?.ok) setContact(j.contact); })
      .catch(() => {});
  }, []);

  // Scroll to specific section on hash anchor (#terms, #privacy, #tc-rfid-consent, etc.)
  useEffect(() => {
    if (!doc) return;
    const hash = window.location.hash;
    if (!hash) return;
    setTimeout(() => {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 60);
  }, [doc]);

  return (
    <div className="min-h-screen bg-slate-50" data-testid="legal-page">
      <header className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-3">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
            data-testid="legal-back-button"
          >
            <ArrowLeft className="w-4 h-4" /> Back to home
          </button>
          <div className="flex items-center gap-2">
            <ScrollText className="w-4 h-4 text-gk-teal" />
            <span className="text-xs sm:text-sm font-semibold text-slate-700">GateKeeper Pro</span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {error ? (
          <div className="rounded-lg bg-white border border-slate-200 p-8 text-center">
            <p className="text-slate-600">Could not load Terms & Conditions. Please try again later.</p>
          </div>
        ) : !doc ? (
          <div className="rounded-lg bg-white border border-slate-200 p-8 text-center">
            <p className="text-slate-500">Loading…</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 sm:p-10">
            <div className="mb-7 pb-5 border-b border-slate-200">
              <h1 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-2" data-testid="legal-title">
                Terms & Conditions
              </h1>
              <p className="text-sm text-slate-600">
                These Terms govern your access to and use of GateKeeper Pro and apply to all residents, management staff, security guards, and visitors interacting with the Platform.
              </p>
              <div className="mt-3 flex items-center gap-3 text-xs text-slate-500">
                <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="legal-version">
                  <Calendar className="w-3 h-3" />
                  Updated {doc.effective_date}
                </span>
                <span data-testid="legal-version-tag">v{doc.version}</span>
              </div>
            </div>

            <div id="terms" />
            <div id="privacy" />

            {(doc.sections || []).map((s) => (
              <Section key={s.id} section={s} contact={contact} />
            ))}

            <div className="text-center text-xs text-slate-400 pt-4 border-t border-slate-100 mt-8">
              — End of document —
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
